import { useSession } from '@batiflow/client-core/react';
import { FormField, isolate } from '@batiflow/ui';
import {
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogBody,
  DialogContent,
  DialogSurface,
  DialogTitle,
  MessageBar,
  MessageBarBody,
  Radio,
  RadioGroup,
  Textarea,
  __css,
  mergeClasses,
  tokens,
} from '@fluentui/react-components';
import {
  CheckmarkCircle20Filled,
  DismissCircle20Filled,
  Warning20Filled,
} from '@fluentui/react-icons';
import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { useNotify } from '@/shared/hooks/use-notify';
import { isStepUpRequired } from '../model';
import { ApprovalStatusBadge } from './approval-badges';
import { ApprovalStepper } from './approval-stepper';
import { useDecideApproval, useRefreshApprovals, useVersionAnnotations } from './approvals-api';
import {
  COMMENT_MAX,
  DECISIONS,
  activeStep,
  approvalErrorMessage,
  decisionBody,
  documentLabel,
  hasObservation,
  isApprovalClosed,
  isDecision,
  isNotYourTurn,
  needsObservation,
  type ApprovalDecisionValue,
  type ApprovalFlow,
} from './approvals-model';

const useStyles = __css({"body":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1noh358"},"context":{"mc9l5x":"f22iagw","Eh141a":"fni485r","Bt984gj":"f122n59","i8kkvl":"fsnqrgy","Belr9w4":"f778t4r"},"code":{"Bahqtrf":"f1fedwem","Bhrd7zp":"fl43uef"},"hint":{"Bo78w4l":0,"Cgygva":0,"yil37a":"f8ntp42","sj55zd":"fkfq4zb"},"choices":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1wn3y0l"},"card":{"Dbcxam":0,"rjzwhg":0,"Bblux5w":"f9b8bpc","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"f12n3xlw","Beyfa6y":0,"Bbmb7ep":0,"Btl43ni":0,"B7oj6ja":0,"Dimara":"ft85np5","gggo0z":"fi15kbk","Behc1zw":"f10zy6vx","B4m11q7":"f32hy0g","tzxy30":"fgmlvap","B834pou":"f1taw3pw","uk9mye":"f1p2dij1","rm1204":"fwyddsq","Bwezv66":"fwl1lcr","F1poyb":"foi3q75","mbw9mh":"f1a566p","ijz38e":"f1ftuoky","Ctt3hk":"f1crbv9w"},"cardSelected":{"De3pzq":"f16xkysk","F1poyb":"fo0u7mh","mbw9mh":"fh32zs","ijz38e":"fyvlgxw","Ctt3hk":"f9srtw2"},"cardLabel":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9"},"cardTitle":{"mc9l5x":"f22iagw","Bt984gj":"f122n59","i8kkvl":"f1ufnopg","Bhrd7zp":"fl43uef"},"cardHint":{"sj55zd":"fkfq4zb","Be2twd7":"fy9rknc"},"textarea":{"Bns2gvi":"f1c209jx"},"annotations":{"mc9l5x":"f22iagw","Beiy3e4":"f1vx9l62","Belr9w4":"f1j0q4x9","Bokbzmb":0,"Jyy4pa":0,"B1u1w3b":"f1r0i6kl","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw","gggo0z":"f1ldemn7","Behc1zw":"f7kxal1","B4m11q7":"f1ea0rl6","tzxy30":"fl6yf4s"},"legend":{"Bhrd7zp":"fl43uef","Bnt446c":0,"B1hrpyx":0,"Fjva8n":"fgdu4nw"}});

const ICONS: Record<ApprovalDecisionValue, ReactElement> = {
  vso: <CheckmarkCircle20Filled />,
  vao: <Warning20Filled />,
  ref: <DismissCircle20Filled />,
};

/** A long observation is cut in the checklist (the whole text is in the viewer). */
const excerpt = (text: string, max = 80): string =>
  text.length > max ? `${text.slice(0, max - 1).trimEnd()}…` : text;

export interface DecisionDialogProps {
  flow: ApprovalFlow;
  onClose: () => void;
}

/**
 * The reviewer's decision on the active step: VSO (approved), VAO (approved with observations) or
 * REF (refused) as cards that explain themselves, an observation (required for VAO and REF unless
 * annotations are linked), and the reviewer's own annotations on this version to link. A decision
 * cannot be changed afterwards, and the dialog says so.
 */
export function DecisionDialog({ flow, onClose }: DecisionDialogProps) {
  const { t } = useTranslation('documents');
  const styles = useStyles();
  const notify = useNotify();
  const userId = useSession((s) => s.me?.user.id);
  const decide = useDecideApproval();
  const refresh = useRefreshApprovals(flow.spaceId);
  const annotations = useVersionAnnotations(flow.spaceId, flow.documentId, flow.versionId);
  const step = activeStep(flow);

  const [decision, setDecision] = useState<ApprovalDecisionValue | null>(null);
  const [comment, setComment] = useState('');
  const [linked, setLinked] = useState<string[]>([]);
  const [touched, setTouched] = useState(false);
  const [failure, setFailure] = useState<string | null>(null);

  const own = (annotations.data?.items ?? []).filter((item) => item.authorId === userId);
  const missingObservation =
    decision !== null && needsObservation(decision) && !hasObservation(comment, linked);
  const label = isolate(documentLabel(flow.documentCode, flow.documentTitle));

  const submit = async () => {
    setTouched(true);
    if (!step || decision === null || missingObservation || decide.isPending) return;
    setFailure(null);
    try {
      await decide.mutateAsync({
        spaceId: flow.spaceId,
        stepId: step.id,
        data: decisionBody(decision, comment, linked),
      });
      await refresh();
      notify({
        intent: 'success',
        message: t('approvals.decide.done', {
          decision: t(`approvals.decisions.${decision}.short`),
        }),
      });
      onClose();
    } catch (error) {
      if (isStepUpRequired(error)) return;
      setFailure(approvalErrorMessage(t, error));
      // The flow moved on meanwhile: the screens show its new state.
      if (isNotYourTurn(error) || isApprovalClosed(error)) await refresh();
    }
  };

  return (
    <Dialog
      open
      modalType="modal"
      onOpenChange={(_event, data) => {
        if (!data.open && !decide.isPending) onClose();
      }}
    >
      <DialogSurface>
        <DialogBody>
          <DialogTitle>{t('approvals.decide.title', { document: label })}</DialogTitle>
          <DialogContent>
            <div className={styles.body}>
              <div className={styles.context}>
                <ApprovalStatusBadge status={flow.status} />
                {flow.revisionIndex ? (
                  <span>
                    {t('approvals.decide.index')} <bdi dir="ltr">{flow.revisionIndex}</bdi>
                  </span>
                ) : null}
              </div>
              <ApprovalStepper flow={flow} currentUserId={userId} />
              <p className={styles.hint}>{t('approvals.decide.intro')}</p>

              <RadioGroup
                className={styles.choices}
                aria-label={t('approvals.decide.choiceLabel')}
                value={decision ?? ''}
                onChange={(_event, data) => {
                  if (isDecision(data.value)) setDecision(data.value);
                  setFailure(null);
                }}
              >
                {DECISIONS.map((value) => (
                  <Radio
                    key={value}
                    value={value}
                    className={mergeClasses(styles.card, decision === value && styles.cardSelected)}
                    label={
                      <span className={styles.cardLabel}>
                        <span className={styles.cardTitle}>
                          <span aria-hidden="true">{ICONS[value]}</span>
                          {t(`approvals.decisions.${value}.chip`)}
                        </span>
                        <span className={styles.cardHint}>
                          {t(`approvals.decisions.${value}.hint`)}
                        </span>
                      </span>
                    }
                  />
                ))}
              </RadioGroup>
              {touched && decision === null ? (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>{t('approvals.decide.choiceRequired')}</MessageBarBody>
                </MessageBar>
              ) : null}

              <FormField
                label={t('approvals.decide.commentLabel')}
                required={decision !== null && needsObservation(decision)}
                hint={
                  decision !== null && needsObservation(decision)
                    ? t('approvals.decide.commentRequiredHint')
                    : t('approvals.decide.commentHint')
                }
                error={touched && missingObservation ? t('approvals.decide.commentRequired') : null}
              >
                <Textarea
                  className={styles.textarea}
                  maxLength={COMMENT_MAX}
                  value={comment}
                  onChange={(_event, data) => {
                    setComment(data.value);
                  }}
                />
              </FormField>

              {own.length > 0 ? (
                <fieldset className={styles.annotations}>
                  <legend className={styles.legend}>
                    {t('approvals.decide.annotationsLegend')}
                  </legend>
                  <p className={styles.hint}>{t('approvals.decide.annotationsHint')}</p>
                  {own.map((item) => (
                    <Checkbox
                      key={item.id}
                      checked={linked.includes(item.id)}
                      label={t('approvals.decide.annotationLine', {
                        page: item.page,
                        comment: isolate(excerpt(item.comment)),
                      })}
                      onChange={(_event, data) => {
                        setLinked((current) =>
                          data.checked === true
                            ? [...current, item.id]
                            : current.filter((id) => id !== item.id),
                        );
                      }}
                    />
                  ))}
                </fieldset>
              ) : null}

              {failure ? (
                <MessageBar intent="error" layout="multiline">
                  <MessageBarBody>{failure}</MessageBarBody>
                </MessageBar>
              ) : null}
            </div>
          </DialogContent>
          <DialogActions>
            <Button appearance="secondary" onClick={onClose} disabled={decide.isPending}>
              {t('common:actions.cancel')}
            </Button>
            <Button
              appearance="primary"
              disabledFocusable={decide.isPending || !step}
              onClick={() => {
                void submit();
              }}
            >
              {decide.isPending ? t('approvals.decide.sending') : t('approvals.decide.send')}
            </Button>
          </DialogActions>
        </DialogBody>
      </DialogSurface>
    </Dialog>
  );
}


import "/opt/batiflow/app/apps/web/src/features/documents/approvals/decision-dialog.griffel.css";